import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAdminMe, fetchAdminAuthLog } from "@/lib/api";
import {
  ShieldAlert, ShieldCheck, Loader2, Lock, Phone, Mail, RefreshCw,
  CheckCircle2, XCircle, KeyRound,
} from "lucide-react";
import { AdminSelfiesPanel } from "@/components/AdminSelfiesPanel";

const EVENT_LABELS = {
  login_password: { label: "Connexion mot de passe", icon: Mail },
  login_phone: { label: "Connexion téléphone (OTP)", icon: Phone },
  otp_send: { label: "Envoi code OTP", icon: KeyRound },
  payment_fraud_alert: { label: "⚠️ Paiement suspect", icon: ShieldAlert },
  biometric_register: { label: "Activation biométrie", icon: ShieldCheck },
  biometric_verify: { label: "Vérification empreinte", icon: ShieldCheck },
  biometric_selfie: { label: "Selfie de contrôle", icon: ShieldCheck },
  passkey_login: { label: "Connexion passkey", icon: KeyRound },
};

export default function AdminSecurityPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState("");
  const [failedOnly, setFailedOnly] = useState(false);

  useEffect(() => {
    fetchAdminMe()
      .then(() => setAuthChecked(true))
      .catch(() => navigate("/", { replace: true }));
  }, [navigate]);

  const load = () => {
    setLoading(true);
    fetchAdminAuthLog({ event: eventFilter, failed_only: failedOnly, limit: 200 })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!authChecked) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, eventFilter, failedOnly]);

  if (!authChecked) {
    return (
      <main className="max-w-7xl mx-auto px-6 py-20 text-center">
        <Loader2 className="animate-spin mx-auto text-orange-500" />
      </main>
    );
  }

  const stats = data?.stats_24h || {};
  const events = data?.events || [];
  const failed24 = stats.failed_attempts ?? 0;
  const locked = stats.locked_accounts ?? 0;

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-security-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight inline-flex items-center gap-3">
            <ShieldAlert size={32} className="text-red-500" /> Sécurité
          </h1>
          <p className="text-[var(--ink-soft)] mt-2">
            Journal des tentatives d&apos;authentification (30 jours) — détection anti-fraude en temps réel.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-500 text-white font-semibold text-sm hover:bg-orange-600 transition-colors"
          data-testid="security-refresh-btn"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>

      {/* Stats 24h */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8" data-testid="security-stat-cards">
        <div className={`glass-card p-5 ${failed24 > 20 ? "ring-2 ring-red-400" : ""}`} data-testid="stat-failed-24h">
          <div className="inline-flex items-center gap-2 text-red-600">
            <XCircle size={16} />
            <span className="text-[10px] uppercase tracking-widest font-bold">Échecs (24h)</span>
          </div>
          <div className="text-3xl font-extrabold mt-2">{failed24.toLocaleString("fr-FR")}</div>
          <div className="text-xs text-[var(--ink-soft)] mt-0.5">
            {failed24 > 20 ? "⚠️ Pic anormal — possible attaque" : "tentatives échouées"}
          </div>
        </div>
        <div className={`glass-card p-5 ${locked > 0 ? "ring-2 ring-amber-400" : ""}`} data-testid="stat-locked-accounts">
          <div className="inline-flex items-center gap-2 text-amber-600">
            <Lock size={16} />
            <span className="text-[10px] uppercase tracking-widest font-bold">Comptes verrouillés</span>
          </div>
          <div className="text-3xl font-extrabold mt-2">{locked.toLocaleString("fr-FR")}</div>
          <div className="text-xs text-[var(--ink-soft)] mt-0.5">verrouillage 15 min (anti brute-force)</div>
        </div>
        <div className="glass-card p-5" data-testid="stat-events-shown">
          <div className="inline-flex items-center gap-2 text-emerald-600">
            <ShieldCheck size={16} />
            <span className="text-[10px] uppercase tracking-widest font-bold">Événements affichés</span>
          </div>
          <div className="text-3xl font-extrabold mt-2">{events.length.toLocaleString("fr-FR")}</div>
          <div className="text-xs text-[var(--ink-soft)] mt-0.5">200 max, du plus récent au plus ancien</div>
        </div>
      </section>

      {/* Filtres */}
      <section className="mt-6 flex flex-wrap items-center gap-3" data-testid="security-filters">
        <select
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border border-orange-200 bg-white text-sm font-semibold"
          data-testid="security-event-filter"
        >
          <option value="">Tous les événements</option>
          <option value="login_password">Connexion mot de passe</option>
          <option value="login_phone">Connexion téléphone (OTP)</option>
          <option value="otp_send">Envoi code OTP</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm font-semibold cursor-pointer select-none">
          <input
            type="checkbox"
            checked={failedOnly}
            onChange={(e) => setFailedOnly(e.target.checked)}
            className="w-4 h-4 accent-red-500"
            data-testid="security-failed-only-checkbox"
          />
          Échecs uniquement
        </label>
      </section>

      {/* Journal */}
      <section className="glass-card p-5 mt-6" data-testid="security-auth-log">
        <h2 className="font-bold text-lg mb-4">Journal d&apos;authentification</h2>
        {loading ? (
          <div className="py-10 text-center">
            <Loader2 className="animate-spin mx-auto text-orange-500" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-sm text-[var(--ink-soft)] py-8 text-center" data-testid="security-empty-state">
            Aucun événement pour ces filtres.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ink-soft)] border-b border-orange-100">
                  <th className="py-2 pr-3">Statut</th>
                  <th className="py-2 pr-3">Événement</th>
                  <th className="py-2 pr-3">Identifiant</th>
                  <th className="py-2 pr-3 hidden md:table-cell">IP</th>
                  <th className="py-2 pr-3 hidden lg:table-cell">Détail</th>
                  <th className="py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev, i) => {
                  const meta = EVENT_LABELS[ev.event] || { label: ev.event, icon: ShieldCheck };
                  const Icon = meta.icon;
                  return (
                    <tr key={i} className="border-b border-orange-50 hover:bg-orange-50/40" data-testid={`auth-event-row-${i}`}>
                      <td className="py-2.5 pr-3">
                        {ev.ok ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold text-xs">
                            <CheckCircle2 size={13} /> OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600 font-semibold text-xs">
                            <XCircle size={13} /> Échec
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className="inline-flex items-center gap-1.5 font-semibold">
                          <Icon size={13} className="text-orange-500" /> {meta.label}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 font-mono text-xs">{ev.phone || ev.email || "—"}</td>
                      <td className="py-2.5 pr-3 font-mono text-xs hidden md:table-cell">{ev.ip || "—"}</td>
                      <td className="py-2.5 pr-3 text-xs text-[var(--ink-soft)] hidden lg:table-cell">{ev.detail || "—"}</td>
                      <td className="py-2.5 text-xs whitespace-nowrap">{formatDate(ev.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-xs text-[var(--ink-soft)] mt-4">
        🛡️ Protections actives : verrouillage 15 min après 5 échecs de mot de passe · max 3 codes OTP/heure/numéro ·
        mots de passe compromis (fuites connues) refusés à l&apos;inscription · journal conservé 30 jours.
      </p>
      <AdminSelfiesPanel />
    </main>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z").toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
